import { createContext, useContext, useEffect, useState } from "react"

type Theme = "dark" | "light" | "system"

type ThemeProviderProps = {
    children: React.ReactNode
    defaultTheme?: Theme
    storageKey?: string
}

type ThemeProviderState = {
    theme: Theme
    setTheme: (theme: Theme) => void
}

const initialState: ThemeProviderState = {
    theme: "system",
    setTheme: () => null,
}

const ThemeProviderContext = createContext<ThemeProviderState>(initialState)

export function ThemeProvider({
    children,
    defaultTheme = "system",
    storageKey = "vite-ui-theme",
}: ThemeProviderProps) {
    const [theme, setTheme] = useState<Theme>(() => {
        // During SSR or prerendering, always use defaultTheme
        if (typeof window === "undefined") return defaultTheme;

        // On client-side initial render, also use defaultTheme to match SSR
        // We'll update from localStorage in useEffect
        return defaultTheme;
    });

    const [mounted, setMounted] = useState(false);

    // Load theme from localStorage after mount (client-side only)
    useEffect(() => {
        setMounted(true);

        try {
            const savedTheme = localStorage.getItem(storageKey) as Theme;
            if (savedTheme && (savedTheme === "dark" || savedTheme === "light" || savedTheme === "system")) {
                setTheme(savedTheme);
            }
        } catch (e) {
            // localStorage might not be available
            console.warn("Failed to load theme from localStorage:", e);
        }
    }, [storageKey]);

    // Apply theme to DOM
    useEffect(() => {
        if (!mounted) return;

        const root = window.document.documentElement;

        root.classList.remove("light", "dark");

        if (theme === "system") {
            const systemTheme = window.matchMedia("(prefers-color-scheme: dark)")
                .matches
                ? "dark"
                : "light";

            root.classList.add(systemTheme);
            return;
        }

        root.classList.add(theme);
    }, [theme, mounted]);

    const value = {
        theme,
        setTheme: (theme: Theme) => {
            try {
                localStorage.setItem(storageKey, theme);
            } catch (e) {
                console.warn("Failed to save theme to localStorage:", e);
            }
            setTheme(theme);
        },
    };

    return (
        <ThemeProviderContext.Provider {...{ value }}>
            {children}
        </ThemeProviderContext.Provider>
    );
}

export const useTheme = () => {
    const context = useContext(ThemeProviderContext)

    if (context === undefined)
        throw new Error("useTheme must be used within a ThemeProvider")

    return context
}
